import { Pencil } from 'lucide-react'
import { Button } from '../../../shared/components/ui/button'
import React, { useState, useEffect } from 'react'
import { Volunteer, NonHomeownerUser, VolunteerTableRow } from '../types/volunteers.type'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
    DialogTrigger,
} from '../../../shared/components/ui/dialog'
import UpdateVolunteerForm from '../forms/volunteer-form/update-volunteer-form'
import UpdateUserForm from '../forms/user-form/update-user-form'
import { getNonHomeownerUserById } from '@/features/volunteers/api/volunteers.api'
import { hydrateVolunteerServeDates } from '../lib/hydrate-volunteer-serve-dates'

// Union type for user data (VolunteerTableRow is minimal table row from table-rows API)
type UserDataItem = Volunteer | NonHomeownerUser | VolunteerTableRow

/** Refetch serve dates before mounting UpdateVolunteerForm (props often lack fresh `serveDates`). */
function useHydratedVolunteerForDialog(volunteer: Volunteer | null, isOpen: boolean) {
    const [loading, setLoading] = React.useState(false)
    const [hydratedVolunteer, setHydratedVolunteer] = React.useState<Volunteer | null>(null)

    React.useEffect(() => {
        if (!isOpen || !volunteer) {
            setHydratedVolunteer(null)
            setLoading(false)
            return
        }
        setLoading(true)
        setHydratedVolunteer(null)
        let cancelled = false
        hydrateVolunteerServeDates(volunteer).then((v) => {
            if (!cancelled) {
                setHydratedVolunteer(v)
                setLoading(false)
            }
        })
        return () => {
            cancelled = true
        }
    }, [isOpen, volunteer?.id, volunteer?.user?.id])

    return { loading, hydratedVolunteer }
}

type EditUserButtonProps = {
    user: UserDataItem
}

type UpdateUserLinkProps = {
    user: UserDataItem
    children: React.ReactNode
}

/** Opens Update User/Volunteer dialog when children (e.g. name) is clicked. Fetches full user on open for fresh data. */
export function UpdateUserLink({ user, children }: UpdateUserLinkProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [fetchedUser, setFetchedUser] = useState<NonHomeownerUser | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const isVolunteer = 'user' in user
    const volunteerEntity = isVolunteer ? (user as Volunteer) : null
    const { loading: volHydrateLoading, hydratedVolunteer } = useHydratedVolunteerForDialog(
        volunteerEntity,
        isOpen && isVolunteer,
    )

    useEffect(() => {
        if (!isOpen || isVolunteer) return
        const userId = user.id
        setLoading(true)
        setError(null)
        setFetchedUser(null)
        getNonHomeownerUserById(userId)
            .then((res) => {
                if (res.success && res.data) setFetchedUser(res.data)
                else setError(!res.success ? res.message : 'Failed to load user')
            })
            .catch(() => setError('Failed to load user'))
            .finally(() => setLoading(false))
    }, [isOpen, isVolunteer, user.id])

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="text-primary hover:underline cursor-pointer text-left font-medium"
                >
                    {children}
                </button>
            </DialogTrigger>
            <DialogContent
                className="py-8 max-w-[600px] md:max-w-[900px] lg:max-w-6xl max-h-[80vh] w-[90%] overflow-y-auto grid-rows-[auto_1fr] md:px-8 md:py-10"
                onInteractOutside={(e) => {
                    if ((e.target as HTMLElement)?.closest?.('.pac-container')) {
                        e.preventDefault()
                    }
                }}
            >
                <div className="flex items-center flex-col gap-2 text-center">
                    <DialogTitle className="text-2xl">Update Volunteer</DialogTitle>
                    <DialogDescription>Update Volunteer information.</DialogDescription>
                </div>
                {isVolunteer ? (
                    <>
                        {volHydrateLoading && <p className="text-muted-foreground">Loading…</p>}
                        {hydratedVolunteer && !volHydrateLoading && (
                            <UpdateVolunteerForm
                                volunteer={hydratedVolunteer}
                                onSuccessCallback={() => setIsOpen(false)}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {loading && <p className="text-muted-foreground">Loading…</p>}
                        {error && <p className="text-destructive">{error}</p>}
                        {fetchedUser && !loading && (
                            <UpdateUserForm
                                user={fetchedUser}
                                onSuccessCallback={() => setIsOpen(false)}
                            />
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}

type UpdateUserLinkByUserIdProps = {
    userId: number
    children: React.ReactNode
}

/** Opens Update User dialog when children (e.g. name) is clicked; fetches user by id. */
export function UpdateUserLinkByUserId({ userId, children }: UpdateUserLinkByUserIdProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [user, setUser] = useState<NonHomeownerUser | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!isOpen || !userId) return
        setLoading(true)
        setError(null)
        setUser(null)
        getNonHomeownerUserById(userId)
            .then((res) => {
                if (res.success && res.data) setUser(res.data)
                else setError(!res.success ? res.message : 'Failed to load user')
            })
            .catch(() => setError('Failed to load user'))
            .finally(() => setLoading(false))
    }, [isOpen, userId])

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="text-primary hover:underline cursor-pointer text-left font-medium"
                >
                    {children}
                </button>
            </DialogTrigger>
            <DialogContent
                className="py-8 max-w-[600px] md:max-w-[900px] lg:max-w-6xl max-h-[80vh] w-[90%] overflow-y-auto grid-rows-[auto_1fr] md:px-8 md:py-10"
                onInteractOutside={(e) => {
                    if ((e.target as HTMLElement)?.closest?.('.pac-container')) {
                        e.preventDefault()
                    }
                }}
            >
                <div className="flex items-center flex-col gap-2 text-center">
                    <DialogTitle className="text-2xl">Update Volunteer</DialogTitle>
                    <DialogDescription>Update Volunteer information.</DialogDescription>
                </div>
                {loading && <p className="text-muted-foreground">Loading…</p>}
                {error && <p className="text-destructive">{error}</p>}
                {user && !loading && (
                    <UpdateUserForm
                        user={user}
                        onSuccessCallback={() => setIsOpen(false)}
                    />
                )}
            </DialogContent>
        </Dialog>
    )
}

function EditUserButton({ user }: EditUserButtonProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [fetchedUser, setFetchedUser] = useState<NonHomeownerUser | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const isVolunteer = 'user' in user
    const volunteerEntity = isVolunteer ? (user as Volunteer) : null
    const { loading: volHydrateLoading, hydratedVolunteer } = useHydratedVolunteerForDialog(
        volunteerEntity,
        isOpen && isVolunteer,
    )

    useEffect(() => {
        if (!isOpen || isVolunteer) return
        const userId = user.id
        setLoading(true)
        setError(null)
        setFetchedUser(null)
        getNonHomeownerUserById(userId)
            .then((res) => {
                if (res.success && res.data) setFetchedUser(res.data)
                else setError(!res.success ? res.message : 'Failed to load user')
            })
            .catch(() => setError('Failed to load user'))
            .finally(() => setLoading(false))
    }, [isOpen, isVolunteer, user.id])

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button
                    type="button"
                    variant="link"
                    size="icon"
                    className="w-7 h-7 bg-foreground rounded-md hover:bg-foreground/90"
                >
                    <Pencil className="w-4 text-white" />
                </Button>
            </DialogTrigger>
            <DialogContent
                className="py-8 max-w-[600px] md:max-w-[900px] lg:max-w-6xl max-h-[80vh] w-[90%] overflow-y-auto grid-rows-[auto_1fr] md:px-8 md:py-10"
                onInteractOutside={(e) => {
                    if ((e.target as HTMLElement)?.closest?.('.pac-container')) {
                        e.preventDefault()
                    }
                }}
            >
                <div className="flex items-center flex-col gap-2 text-center">
                    <DialogTitle className="text-2xl">Update Volunteer</DialogTitle>
                    <DialogDescription>Update Volunteer information.</DialogDescription>
                </div>
                {isVolunteer ? (
                    <>
                        {volHydrateLoading && <p className="text-muted-foreground">Loading…</p>}
                        {hydratedVolunteer && !volHydrateLoading && (
                            <UpdateVolunteerForm
                                volunteer={hydratedVolunteer}
                                onSuccessCallback={() => setIsOpen(false)}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {loading && <p className="text-muted-foreground">Loading…</p>}
                        {error && <p className="text-destructive">{error}</p>}
                        {fetchedUser && !loading && (
                            <UpdateUserForm
                                user={fetchedUser}
                                onSuccessCallback={() => setIsOpen(false)}
                            />
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default EditUserButton
